import { useState } from 'react';
import clsx from 'classnames';
import { mixpanelTrack } from '@providers/Mixpanel';
import * as ApiModels from '@typings/api-models';
import FileZip from '@components/Icons/FileZip';
import { getUrlBase } from '@helpers/urlsHelper';
import ProgressIndicator from '@/components/ProgressIndicator';
import { useToast, useUser } from '@/hooks';
import { logError } from '@/providers/ErrorTracking';
import simpleDebug from '@/helpers/simpleDebug';
import * as tsClient from '@/tsClient';
import useNoodleApi from '@/hooks/useNoodleApi';
import removeNullish from '@/helpers/removeNullish';
import s from './DownloadArchive.module.scss';

type Props = {
  accessToken?: string;
  className?: string;
  userWorkflow: {
    id: string;
    product: Pick<ApiModels.Product, 'title'> | null;
    workflow: Pick<ApiModels.Workflow, 'name'>;
    person: Pick<ApiModels.Person, 'name' | 'email'>;
  };
  documents: { path: string[]; id: string }[];
};

const DownloadZipWithDirectories: React.FC<Props> = ({ accessToken, className, documents, userWorkflow }) => {
  const [user] = useUser();
  const authToken = user?.token;
  const addToast = useToast();
  const [isZIPLoading, setIsZIPLoading] = useState(false);
  const { getData: createArchive } = useNoodleApi(tsClient.archives.createArchive);

  if (documents.length === 0) {
    return null;
  }

  const handleZIPClick = async (): Promise<void> => {
    if (authToken) {
      const archiveName = [
        userWorkflow.person.name || userWorkflow.person.email || userWorkflow.id,
        userWorkflow.product?.title,
        userWorkflow.workflow.name,
      ].filter(removeNullish).join(' - ');
      mixpanelTrack('Workflow - Download archive as ZIP with directories', { archiveName, userWorkflowId: userWorkflow.id });
      setIsZIPLoading(true);
      simpleDebug('downloadArchive', 'zip start', { archiveName });

      const createArchiveResponse = await createArchive({
        files: documents.map(document => ({
          documentId: document.id,
          path: document.path,
        })),
        name: archiveName,
      });

      if (createArchiveResponse.data) {
        const zipDownloadLink = getUrlBase(`/documents-proxy/archives/${createArchiveResponse.data.id}/zip`, {
          accessToken,
        });

        const a = window.document.createElement('a');
        a.href = zipDownloadLink;
        a.style.display = 'none';
        a.download = `${createArchiveResponse.data.name}.zip`;
        window.document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(zipDownloadLink);
        a.remove();

        // There must be a better way of showing the toast on complete than also downloading through xhr and throwing that data away.
        const xhr = new XMLHttpRequest();
        xhr.open('GET', zipDownloadLink, true);

        xhr.onload = () => {
          simpleDebug('downloadArchive', 'zip done');
          addToast(useToast.ToastTypeVariants.SUCCESS, 'Downloaded files as ZIP');
          setIsZIPLoading(false);
        };

        xhr.onerror = (error) => {
          simpleDebug('downloadArchive', 'zip error', error);
          logError(error, {
            flow: 'workflows',
            userWorkflowId: userWorkflow.id,
          });
          addToast(useToast.ToastTypeVariants.ERROR, 'Failed to download files as ZIP');
          setIsZIPLoading(false);
        };

        xhr.setRequestHeader('Authorization', authToken);

        xhr.send();
      } else {
        logError(createArchiveResponse.error, {
          flow: 'workflows',
          userWorkflowId: userWorkflow.id,
        });
        setIsZIPLoading(false);
      }
    }
  };

  return (
    <a
      className={clsx(s['download-link'], className)}
      download
      data-cy="downloadZip"
      onClick={handleZIPClick}
      style={{ opacity: isZIPLoading ? 0.6 : 1, pointerEvents: isZIPLoading ? 'none' : 'auto' }}
    >
      <FileZip /> Download all as a ZIP
      {isZIPLoading && <ProgressIndicator />}
    </a>
  );
};

export default DownloadZipWithDirectories;
